import React, { useEffect, useRef, useState } from 'react';
import { ClipRating as ClipRatingModel, draftRatingReason, getClipRating, rateClip } from '../../api/ideaLibrary';

interface ClipRatingProps {
  clipId: string;
}

/**
 * The verdict control: 1-5 stars plus the one-line "why", living where the
 * finished video is watched. A star click saves immediately; the note saves
 * on blur. "Draft why" fills the input from the clip's idea and NEVER saves
 * by itself — the human's edit is the point. The "saved" chip describes the
 * CURRENT state, so any edit clears it until the next save lands.
 *
 * The rating loads ONCE per clipId (effect keyed on the id, nothing else) —
 * never on parent re-renders with fresh prop identities. That exact identity
 * churn turned two frame lookups into a request flood on 2026-09-01.
 */
const ClipRating: React.FC<ClipRatingProps> = ({ clipId }) => {
  const [score, setScore] = useState(0);
  const [note, setNote] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [drafting, setDrafting] = useState(false);
  const noteDirty = useRef(false);
  // Set the moment the human touches the control. A load that resolves after
  // that must not replace what they just chose with the older server value.
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getClipRating(clipId)
      .then((rating: ClipRatingModel | null) => {
        if (cancelled || !rating || touched.current) return;
        setScore(rating.score);
        setNote(rating.note || '');
      })
      .catch(() => {
        // An unreadable rating renders the empty control; rating again heals it.
      });
    return () => {
      cancelled = true;
    };
  }, [clipId]);

  const save = async (nextScore: number, nextNote: string) => {
    setError('');
    try {
      await rateClip(clipId, { score: nextScore, note: nextNote });
      noteDirty.current = false;
      setSavedAt(Date.now());
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save rating');
    }
  };

  const pickScore = (value: number) => {
    touched.current = true;
    setSavedAt(null);
    setScore(value);
    void save(value, note);
  };

  const blurNote = () => {
    if (score > 0 && noteDirty.current) {
      void save(score, note);
    }
  };

  const draft = async () => {
    if (score < 1 || drafting) return;
    touched.current = true;
    setDrafting(true);
    setError('');
    try {
      const drafted = await draftRatingReason(clipId, score);
      setNote(drafted);
      noteDirty.current = true; // filled, not saved — blur (or a star) commits it
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3" data-testid="clip-rating">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Rate this idea
        </span>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              aria-label={`Rate ${value}`}
              aria-pressed={value <= score}
              onClick={() => pickScore(value)}
              className={`px-0.5 text-base leading-none transition-colors ${
                value <= score ? 'text-amber-300' : 'text-zinc-600 hover:text-zinc-400'
              }`}
            >
              ★
            </button>
          ))}
        </div>
        {savedAt ? <span className="text-[10px] text-zinc-500">saved</span> : null}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={note}
          maxLength={200}
          placeholder="why did it work (or not)? — this teaches future discussions"
          aria-label="Rating note"
          onChange={(e) => {
            touched.current = true;
            setSavedAt(null);
            setNote(e.target.value);
            noteDirty.current = true;
          }}
          onBlur={blurNote}
          className="w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600"
        />
        <button
          type="button"
          onClick={() => void draft()}
          disabled={score < 1 || drafting}
          title={score < 1 ? 'Pick a score first' : 'Draft the why from this clip’s idea'}
          className="shrink-0 rounded border border-white/15 bg-black/40 px-2 py-1 text-[11px] text-zinc-300 hover:text-white disabled:opacity-40"
        >
          {drafting ? 'Drafting…' : 'Draft why'}
        </button>
      </div>
      {error ? <p className="text-[10px] text-red-400">{error}</p> : null}
    </div>
  );
};

export default ClipRating;
